import { MelodyLib } from '@tunasong/music-lib'
import type { Melody } from '@tunasong/schemas'
import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { Keyboard } from '../keyboard/keyboard.js'
import type { KeyboardNoteInfo } from '../keyboard/keyboard.js'
import { MelodyCapture } from '../melody-capture/melody-capture.js'
import { MelodyTrainerDialog } from '../melody-trainer/melody-trainer-dialog.js'
import { midiInsert } from './melody-midi-insert.js'
import styles from './melody-editor.module.css'
import { MelodyRoll } from './melody-roll.js'
import { MelodyRollHeader } from './melody-roll-header.js'
import { useMelodyMidiInsert } from './use-melody-midi-insert.js'
import { useMelodyRollPlayback } from './use-melody-roll-playback.js'
import { VelocityLane } from './velocity-lane.js'

/**
 * The full melody editor: header, roll, velocity lane, on-screen keyboard, MIDI insert, transport.
 *
 * It owns exactly three pieces of state, none of which belong to the melody: the insert cursor,
 * the selected note, and the "quantized" A/B toggle. Everything else is a pure `MelodyLib` op
 * against the melody the caller passed in, returned through `onChange` - so the entity is the one
 * copy of the truth and there is no local draft to reconcile.
 *
 * The invariant readout is deliberately visible rather than silently repaired: a melody arriving
 * off-grid means something upstream (a converter, or one day the §5 quantizer) is wrong, and
 * quietly snapping it would hide that.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §4
 */

export interface MelodyEditorProps {
  melody: Melody
  onChange: (melody: Melody) => void
  /**
   * Where a capture's recording is filed - the melody's own entity, or the document an embedded
   * melody sits in. Absent means the take's audio is saved without a parent.
   */
  captureParentId?: string
  className?: string
}

export const MelodyEditor: FC<MelodyEditorProps> = props => {
  const { melody, onChange, captureParentId, className } = props

  const [cursorBeats, setCursorBeats] = useState(0)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const [quantized, setQuantized] = useState(false)
  const [capturing, setCapturing] = useState(false)
  const [practising, setPractising] = useState(false)

  const playback = useMelodyRollPlayback({ melody, quantized })

  useMelodyMidiInsert({ melody, cursorBeats, onChange, onCursorChange: setCursorBeats })

  const handleKeyboardNote = ({ midiNumber }: KeyboardNoteInfo) => {
    /* An on-screen key has no velocity, so it takes the same default a converted suggestion does. */
    const result = midiInsert(melody, cursorBeats, midiNumber, MelodyLib.SUGGESTION_VELOCITY)
    onChange(result.melody)
    setCursorBeats(result.cursorBeats)
  }

  const issues = MelodyLib.validateMelody(melody)

  return (
    <div className={className ? `${styles.root} ${className}` : styles.root}>
      <MelodyRollHeader melody={melody} onChange={onChange} quantized={quantized} onQuantizedChange={setQuantized} />

      <div className={styles.transport}>
        <Button onClick={playback.isPlaying ? playback.stop : playback.play}>
          {playback.isPlaying ? 'Stop' : 'Play'}
        </Button>
        <span className={styles.cursor}>{`Insert at beat ${cursorBeats + 1}`}</span>
        <Button onClick={() => setCursorBeats(0)}>Cursor to start</Button>
        {/* Capture replaces the notes wholesale, so it lives beside the transport and not in the roll. */}
        <Button onClick={() => setCapturing(true)}>Capture</Button>
        {/*
         * Practice sits beside Capture rather than in `MelodyRollHeader`: that header holds the
         * melody's FIELDS, and a control that neither reads nor writes one does not belong there.
         */}
        {melody.notes.length > 0 ? <Button onClick={() => setPractising(true)}>Practice</Button> : null}
      </div>

      <MelodyCapture
        melody={melody}
        open={capturing}
        parentId={captureParentId}
        onCapture={onChange}
        onClose={() => setCapturing(false)}
      />

      <MelodyTrainerDialog melody={melody} open={practising} onClose={() => setPractising(false)} />

      {issues.length > 0 ? (
        <ul className={styles.issues} role="status">
          {issues.map(issue => (
            <li key={`${issue.kind}-${issue.noteIndex}`}>{issue.message}</li>
          ))}
        </ul>
      ) : null}

      <MelodyRoll
        melody={melody}
        onChange={onChange}
        quantized={quantized}
        playheadMs={playback.playheadMs}
        cursorBeats={cursorBeats}
        onCursorChange={setCursorBeats}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
      />

      <VelocityLane melody={melody} onChange={onChange} selectedIndex={selectedIndex} onSelect={setSelectedIndex} />

      <div className={styles.keyboard} data-testid="melody-keyboard">
        <Keyboard scale={melody.key ?? null} onPlayNote={handleKeyboardNote} />
      </div>
    </div>
  )
}
